<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'

import UiIcon from '@/components/common/UiIcon.vue'
import { localizedPagePath } from '@/router/pageSlugs'

import type { DesktopNavCard, DesktopNavKey } from './desktopNav'

const props = defineProps<{
  activeNav: DesktopNavKey | null
  cardsByNav: Record<DesktopNavKey, DesktopNavCard[]>
}>()

const emit = defineEmits<{
  (e: 'advance-nav'): void
  (e: 'close'): void
  (e: 'follow-link'): void
  (e: 'return-to-trigger'): void
}>()

const { t, locale } = useI18n()

const activeDesktopNavId = computed(() =>
  props.activeNav ? `header-${props.activeNav}-nav` : ''
)

const activeOverviewLinkId = computed(() =>
  props.activeNav ? `header-${props.activeNav}-overview-link` : ''
)

const activeDesktopNavLabel = computed(() => {
  if (props.activeNav === 'trainer') return t('menu.trainer')
  return t('menu.gym')
})

const activeDesktopNavIcon = computed(() =>
  props.activeNav === 'trainer' ? 'Users' : 'Building2'
)

const activeOverviewPath = computed(() => {
  void locale.value

  if (props.activeNav === 'trainer') {
    return localizedPagePath('trainerOverview')
  }

  return localizedPagePath('gymOverview')
})

const activeOverviewLabel = computed(() => {
  if (props.activeNav === 'trainer') return t('menu.trainer')
  return t('footer.gyms')
})

const activeDesktopCards = computed<DesktopNavCard[]>(() => {
  if (!props.activeNav) return []
  return props.cardsByNav[props.activeNav]
})

function handleOverviewTab(event: KeyboardEvent) {
  if (activeDesktopCards.value.length > 0) return

  event.preventDefault()
  emit('advance-nav')
}

function handleCardTab(event: KeyboardEvent, index: number) {
  if (index !== activeDesktopCards.value.length - 1) return

  event.preventDefault()
  emit('advance-nav')
}
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-0"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition duration-250 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-0"
  >
    <nav
      v-if="activeNav"
      :id="activeDesktopNavId"
      :key="activeNav"
      :aria-label="activeDesktopNavLabel"
      class="absolute inset-x-0 top-full z-20 hidden rounded-b-2xl bg-background shadow-2xl lg:block"
    >
      <div class="mx-auto max-w-screen-2xl px-6 py-6">
        <div class="flex flex-wrap items-center justify-start gap-4 pb-4">
          <div class="flex items-center gap-3">
            <UiIcon
              :name="activeDesktopNavIcon"
              :size="20"
              class="shrink-0 text-foreground"
            />
            <p class="text-sm font-semibold uppercase tracking-[0.18em] text-foreground">
              {{ activeDesktopNavLabel }}
            </p>
          </div>

          <RouterLink
            :id="activeOverviewLinkId"
            :to="activeOverviewPath"
            class="inline-flex items-center rounded-md text-sm font-semibold text-primary transition hover:text-primary-deep hover:underline focus-visible:outline-none focus-visible:underline"
            @keydown.tab.exact="handleOverviewTab"
            @keydown.shift.tab.prevent="emit('return-to-trigger')"
            @click="emit('follow-link')"
          >
            {{ activeOverviewLabel }}
          </RouterLink>
        </div>

        <ul class="mt-6 grid gap-4 lg:grid-cols-4">
          <li
            v-for="(item, index) in activeDesktopCards"
            :key="item.id"
          >
            <RouterLink
              :to="item.to"
              class="group flex h-full flex-col rounded-2xl border border-border bg-card p-4 text-card-foreground transition-colors duration-200 hover:border-primary hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:hover:border-primary dark:hover:bg-primary/15"
              @keydown.tab.exact="handleCardTab($event, index)"
              @click="emit('follow-link')"
            >
              <span class="text-lg font-semibold text-card-foreground">{{ item.title }}</span>
              <span
                v-if="item.description"
                class="mt-2 text-sm leading-relaxed text-muted-foreground"
              >
                {{ item.description }}
              </span>
              <span class="mt-4 text-sm font-medium text-primary group-hover:text-primary-deep group-hover:underline">
                {{ item.ctaLabel }}
              </span>
            </RouterLink>
          </li>
        </ul>

        <p
          v-if="activeDesktopCards.length === 0"
          class="mt-6 text-sm text-muted-foreground dark:text-inherit"
        >
          {{ t('menu.emptyDropdown') }}
        </p>
      </div>
    </nav>
  </Transition>
</template>
